<template>
  <div class="track-package">
    <BreadcrumbWrapper :current-page="currentPage" class="margin-bottom-xs" />
    <div v-if="!isLoading" class="grid-x">
      <!-- left content -->
      <div class="cell small-12 large-10 large-padding-right-xxl">
        <h1 class="margin-bottom-m" :class="{ 'small-margin-bottom-s medium-margin-bottom-m': trackPackageFlags.hasNotification }">
          {{ TRACK_PACKAGE.titleText }}
        </h1>
        <div v-if="trackPackageFlags.hasNotification" class="margin-bottom-m">
          <Notification
            v-if="trackPackageError"
            severity="error"
            :closable="false"
            :class="{
              'margin-bottom-xs': isMarginBottomApplicable,
            }"
          >
            <!-- eslint-disable vue/no-v-html -->
            <div>{{ trackPackageError }}</div>
          </Notification>
          <Notification
            v-if="trackPackageFlags.isBackOrderNotification"
            id="willing-to-wait-msg"
            severity="success"
            :closable="false"
            :class="{
              'margin-bottom-xs': trackPackageFlags.isShipmentReRouted,
            }"
          >
            {{ TRACK_PACKAGE.notification.willingToWaitText }}
          </Notification>
          <Notification v-if="trackPackageFlags.isShipmentReRouted" severity="info" :closable="false">
            {{ TRACK_PACKAGE.notification.shipmentReRoutedText }}
          </Notification>
        </div>
        <div class="grid-y">
          <!-- Start: TOP Section -->
          <div>
            <h2 v-if="trackPackage?.shipmentStatus" class="large margin-bottom-xxs" v-text="trackPackage.shipmentStatus"></h2>
            <div v-if="trackPackage?.shipmentSubStatus" v-text="trackPackage.shipmentSubStatus"></div>
            <div
              v-if="trackPackage?.shipmentSubStatusDetails"
              class="small gray-1-color margin-bottom-xs"
              v-text="trackPackage?.shipmentSubStatusDetails"
            ></div>
            <ProgressBarComponent
              v-if="trackPackage?.shipmentStatus"
              :progress-bar-data="progressBarData"
              :progress-bar-value="shipmentStatus?.statusBarValue"
            />
          </div>
          <!-- Start: Mid section -->
          <div v-if="trackPackageFlags.showTrackingDetailsButton" class="grid-x margin-top-m">
            <div class="cell small-12 medium-3">
              <button
                class="primary button expanded"
                :class="{ 'button-small': !envProps.isMew }"
                type="button"
                @click="getTrackDetails('tracking details')"
              >
                {{ TRACK_PACKAGE.trackingDetailsText }}
              </button>
            </div>
          </div>
          <div
            class="grid-x margin-top-m"
            :class="{
              'small-order-2': trackPackageFlags.isSTHOrderInformationVisible,
            }"
          >
            <div class="cell grid-x medium-8">
              <!-- Column One -->
              <div class="cell medium-6">
                <PickupInformation
                  v-if="isBopsOrBossShipment"
                  :data="{
                    ...trackPackage?.contact?.address,
                    ...trackPackage?.contact,
                    ...trackPackage?.pickupDetails,
                    ...shippingDetails,
                  }"
                />
                <UpsApInformation v-else-if="shipmentType === 'S2AP' && isS2AP" :data="upsApDetails" />
                <DeliveryInformation v-else-if="shipmentType === 'VGC' && vgcEmailAddress" :email-address="vgcEmailAddress" />
                <ShippingInformation v-else :data="trackPackage?.contact" class="margin-bottom-m" />
              </div>
              <!-- Column Two -->
              <div class="cell medium-6">
                <div v-if="isS2AP" class="margin-bottom-m padding-right-xs hide-for-small-only">
                  <picture v-if="storeMapUrl">
                    <source media="(min-width: 102px)" :srcset="storeMapUrl" />
                    <img alt="UPS access point store map" :src="storeMapUrl" />
                  </picture>
                </div>
                <WhereToGo
                  v-if="trackPackageFlags.shouldShowWhereToGo"
                  :pickup-information="trackPackage?.pickupDetails?.pickupInformation"
                  class="margin-bottom-m"
                />
                <OrderInformation v-if="trackPackageFlags.isSTHOrderInformationVisible" class="cell" :order-details="trackPackage" />
              </div>
              <OrderInformation v-if="trackPackageFlags.shouldShowRegularOrderInformation" class="cell" :order-details="trackPackage" />
            </div>

            <!-- Column Three & CTA Button -->
            <div id="cta-section" class="cell medium-4 margin-bottom-s">
              <template v-if="trackPackage?.shipmentStatusCode === 111">
                <template v-if="showWaitForItemButton">
                  <button
                    id="wait-for-item-btn"
                    class="cell button primary"
                    :class="{ 'margin-bottom-xxs': trackPackage?.cancelItemEligibleFlag }"
                    type="button"
                    @click="waitForItem"
                  >
                    {{ TRACK_PACKAGE.waitForItemText }}
                  </button>
                </template>
                <button
                  v-if="trackPackage?.cancelItemEligibleFlag"
                  id="cancel-item-btn"
                  class="cell button secondary cancel-item"
                  type="button"
                  @click="openConfirmCancelItemModal"
                >
                  {{ TRACK_PACKAGE.cancelItemText }}
                </button>
              </template>
              <template v-else-if="trackPackageFlags.isBopsOrBossShipment">
                <div v-if="trackPackage.shipmentStatusCode === 104" class="text-center">
                  <div class="margin-bottom-xxs medium">
                    {{ TRACK_PACKAGE.showBarcodeText }}
                  </div>
                  <BarCode :barcode-number="trackPackage?.reservationNumber[0]?.toString()" do-pad />
                  <a
                    :href="trackPackage?.curbsideCheckinFullUrl"
                    target="_blank"
                    class="primary button expanded margin-bottom-xxs"
                    @click="getCurbsidePickup"
                  >
                    {{ TRACK_PACKAGE.useCurbsideText }}
                  </a>
                  <div>
                    {{ TRACK_PACKAGE.colleagueMeetText }}
                  </div>
                </div>
                <div v-else-if="trackPackageFlags.showConfirmShippingButton">
                  <a id="confirm-your-shipping" class="primary button expanded" :href="trackPackage?.saveTheSaleUrl" target="_blank">
                    {{ TRACK_PACKAGE.confirmShoppingText }}
                  </a>
                </div>
              </template>

              <div v-if="trackPackageFlags.showSddDeliveryInfoButton">
                <button class="primary button expanded" type="button" @click="getTrackDetails('sdd delivery information')">
                  {{ TRACK_PACKAGE.deliveryInformationText }}
                </button>
              </div>
              <div v-else-if="trackPackage?.shipmentStatusCode === 201">
                <button id="schedule-delivery-redirect" class="primary button expanded" @click="scheduleDelivery">
                  {{ TRACK_PACKAGE.scheduleDeliveryText }}
                </button>
              </div>
              <div v-else-if="trackPackageFlags.shouldShowLegacyTrackingButton">
                <button class="primary button expanded" type="button" @click="getTrackDetails('tracking details')">
                  {{ TRACK_PACKAGE.trackingDetailsText }}
                </button>
              </div>
            </div>
          </div>
          <!-- End: Mid section -->
          <div v-if="killSwitch?.ppeTrackPackageResponseOrderBasedEnabled">
            <OrderCard
              v-for="(order, index) in orders"
              :key="index"
              class="margin-bottom-xl"
              :order="order"
              is-track-package
              :is-buy-again-slideout-enabled="killSwitch?.buyAgainSlideoutEnabled"
            />
          </div>
          <ProductThumbnailWrapper
            v-else
            show-buy-again
            :products="transformProducts(trackPackage?.items)"
            :is-red-carpet-shipment="isRedCarpetShipment"
            page="track-package"
            :is-buy-again-slideout-enabled="killSwitch?.buyAgainSlideoutEnabled"
          />
        </div>
        <NeedHelp
          class="text-center align-center padding-top-m border-top"
          :class="{ 'small-margin-bottom-xl padding-bottom-m border-bottom': envProps.isMew }"
          :shipment-type="trackPackage?.primaryShipmentType"
          show-chat
        />
      </div>

      <!-- right panel -->
      <div class="cell small-12 large-2 padding-left-xs">
        <ProsWrapper v-if="hasItemsOrOrders" data-context="PPET_ZONE_A" :killswitches="killSwitch" />
      </div>
    </div>
    <Skeleton v-else />
  </div>
</template>

<script setup>
import { UserStatus, Cookie } from '@vue-util/cookie';
import { computed, onMounted, onUnmounted, markRaw, defineAsyncComponent } from 'vue';
import Notification from '@atomic-ui/notification/Notification';
import { fireTag } from '@vue-util/tagManager';
import { useRoute } from 'vue-router';
import { useTitle } from '@vueuse/core';
import ProductThumbnailWrapper from '@/components/common/ProductThumbnailWrapper';
import ProgressBarComponent from '@/components/ProgressBar';
import BreadcrumbWrapper from '@/components/common/BreadcrumbWrapper';
import { getShipmentStatus, getShipmentTrackingLabel } from '@/utils/ShipmentStatus';
import { transformProducts } from '@/utils/TransformProduct';
import PickupInformation from '@/components/common/PickupInformation';
import UpsApInformation from '@/components/common/UpsApInformation';
import DeliveryInformation from '@/components/common/DeliveryInformation';
import ShippingInformation from '@/components/common/ShippingInformation';
import OrderInformation from '@/components/common/OrderInformation';
import WhereToGo from '@/components/common/WhereToGo';
import NeedHelp from '@/components/common/NeedHelp';
import BarCode from '@/components/common/BarCode';
import ProsWrapper from '@/components/ProsWrapper';
import Skeleton from '@/components/skeleton/TrackPackage';
import OrderCard from '@/components/common/OrderCard';
import { TRACK_PACKAGE, onlineUid, brandName, PPE_ERROR_TAG } from 'config';
import { PPE_SCHEDULE_DELIVERY_URL } from '#shared/endpoints';
import { useTrackPackageStore } from '@/stores/useTrackPackageStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useBackOrderConfirmationStore } from '@/stores/ppe/useBackOrderConfirmationStore';
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
import { useSlideout } from '@/composables/useSlideout';
import { useModal } from '@/composables/useModal';

const currentPage = 'Tracking';

const trackPackageStore = useTrackPackageStore();
const envPropsStore = useEnvPropsStore();
const backOrderConfirmationStore = useBackOrderConfirmationStore();
const orderDetailsStore = useOrderDetailsStore();
const route = useRoute();

const { registerSlideout, toggleSlideout, closeSlideout } = useSlideout();
const { registerModal, toggleModal } = useModal();

const trackPackage = computed(() => trackPackageStore.trackPackage);
const trackPackageError = computed(() => trackPackageStore.trackPackageError);
const isLoading = computed(() => trackPackageStore.isLoading);
const killSwitch = computed(() => trackPackageStore.killSwitch);
const envProps = computed(() => envPropsStore.envProps);
const pageName = computed(() => TRACK_PACKAGE.pageName);
const orders = computed(() => trackPackageStore.trackPackage.orders);
const shipmentType = computed(() => trackPackage.value?.shipmentType);
const shippingDetails = computed(() => ({
  shipmentStatusCode: trackPackage.value?.shipmentStatusCode,
  shipmentType: shipmentType.value,
}));

const isRedCarpetShipment = computed(() => {
  if (!killSwitch.value?.redCarpetMembershipEnabled) {
    return false;
  }
  const shipmentProds = trackPackage.value?.items;
  const envUpcNumber = envProps.value?.redCarpetUpcNumber;
  if (!(shipmentProds && shipmentProds.length) || !envUpcNumber) {
    return false;
  }
  return shipmentProds.some((item) => item.upcNumber && envUpcNumber && `${item.upcNumber}` === `${envUpcNumber}`);
});
const progressBarData = computed(() => getShipmentTrackingLabel(shipmentType.value, trackPackage.value?.shipmentStatusCode, isRedCarpetShipment));
const shipmentStatus = computed(() => getShipmentStatus(trackPackage.value?.shipmentStatusCode, shipmentType.value, isRedCarpetShipment));
const isSignedIn = computed(() => UserStatus.isSignedIn());
const upsApDetails = computed(() => trackPackage.value?.upsApDetails);
const storeMapUrl = computed(() => upsApDetails.value?.storeMapUrl);
const isS2AP = computed(() => !!upsApDetails.value);
const vgcEmailAddress = computed(() => {
  if (shipmentType.value === 'VGC' && killSwitch.value?.ppeTrackPackageResponseOrderBasedEnabled) {
    return trackPackage.value?.orders[0].shipments[0].items[0].emailAddress;
  }
  if (shipmentType.value === 'VGC') {
    return trackPackage.value?.items[0].emailAddress;
  }
  return null;
});
const updateTrackPackageApiError = computed(() => trackPackageStore.updateTrackPackageApiError);
const isMarginBottomApplicable = computed(() => trackPackageFlags.value.isBackOrderNotification || trackPackageFlags.value.isShipmentReRouted);
const updateTrackDetailsApiError = computed(() => trackPackageStore.updateTrackDetailsApiError);
const showWaitForItemButton = computed(
  () => ['backordered_2a', 'backordered_2b'].includes(trackPackage.value?.sortIdentifier) && !trackPackage.value?.willingToWaitInd,
);
const isBopsOrBossShipment = computed(() => ['BOPS', 'BOSS'].includes(shipmentType.value));
const hasItemsOrOrders = computed(() => trackPackage.value?.items?.length || trackPackage.value?.orders?.length);

const triggerSlideout = async () => {
  await registerSlideout({
    id: 'tracking-details-slideout',
    title: 'Tracking Details',
    component: markRaw(defineAsyncComponent(() => import('@/components/ppe/TrackingDetailsSlideout'))),
  });
  toggleSlideout('tracking-details-slideout');
};

const BACKORDER_SORT_IDS = ['backordered_2a', 'backordered_2b'];
const SDD_CODES = [413, 407, 402];
const BOPS_TYPES = ['BOPS', 'BOSS'];

const TRACKING_STATUS_CONDITIONS = {
  BT_VDF: [203, 204, 205],
  STH: [101, 102],
  S2AP: [604, 605],
};

const S2AP_REROUTE_CODES = [604, 605];

const trackPackageFlags = computed(() => {
  const tp = trackPackage.value || {};
  const ks = killSwitch.value || {};
  const stCode = tp.shipmentStatusCode;
  const stType = tp.shipmentType;

  const shouldShowTrackingDetails = !!(tp.trackingNumber && TRACKING_STATUS_CONDITIONS[stType]?.includes(stCode));

  const isSTHOrderInformationVisible = stType === 'STH' && shouldShowTrackingDetails && !ks.ppeTrackPackageResponseOrderBasedEnabled;

  const isShipmentReRouted = stType === 'S2AP' && S2AP_REROUTE_CODES.includes(stCode) && tp.shipmentReRouted;

  const isBackOrderNotification = stCode === 111 && tp.willingToWaitInd;

  return {
    // Notifications
    hasNotification: Boolean(trackPackageError.value) || isBackOrderNotification || isShipmentReRouted,

    isBackOrderNotification,
    isShipmentReRouted,

    // Buttons
    showTrackingDetailsButton: shouldShowTrackingDetails && (ks.ppeTrackPackageResponseOrderBasedEnabled || stType === 'STH'),

    showWaitForItemButton: BACKORDER_SORT_IDS.includes(tp.sortIdentifier) && !tp.willingToWaitInd,

    showConfirmShippingButton: !!(tp.sortIdentifier === 'bops_unavailableforpickup' && tp.saveTheSaleUrl),

    showSddDeliveryInfoButton: !!(stType === 'SDD' && SDD_CODES.includes(stCode)),

    shouldShowLegacyTrackingButton: shouldShowTrackingDetails && stType !== 'STH' && !ks.ppeTrackPackageResponseOrderBasedEnabled,

    // Sections
    isSTHOrderInformationVisible,

    shouldShowRegularOrderInformation: !isSTHOrderInformationVisible && !ks.ppeTrackPackageResponseOrderBasedEnabled,

    shouldShowWhereToGo: !!(BOPS_TYPES.includes(stType) && stCode === 104 && tp.pickupDetails?.pickupInformation),

    isBopsOrBossShipment: BOPS_TYPES.includes(stType),

    shouldShowTrackingDetails,
  };
});

const getTrackDetails = async (name) => {
  fireTag('link', {
    event_name: `${TRACK_PACKAGE.trackDetailsTag.event_name}${name}`,
    link_name: `${TRACK_PACKAGE.trackDetailsTag.custom_link_name}${name}`,
    cta_to_pageview: true,
  });

  await trackPackageStore.GET_TRACKING_DETAILS({
    trackingNumber: trackPackage.value?.trackingNumber,
    carrierName: trackPackage.value?.carrierName,
    isMkpShipment: trackPackage.value?.isMkpShipment,
    isLtlShipment: trackPackage.value?.isLtlShipment,
    shipmentType: shipmentType.value,
  });

  if (updateTrackDetailsApiError.value) {
    fireTag('link', {
      ...PPE_ERROR_TAG.error,
      error_code: [updateTrackDetailsApiError.value.errorCode || PPE_ERROR_TAG.defaultErrorCode],
      error_message: [updateTrackDetailsApiError.value.message],
    });
  }

  await triggerSlideout();
};

const getTrackPackage = async () => {
  await trackPackageStore.GET_TRACKING_PACKAGE_DETAILS({
    orderToken: route.query.o,
    shipmentToken: route.query.s,
    trackingNos: route.query.trackingNos,
  });

  if (updateTrackPackageApiError.value) {
    fireTag('link', {
      ...PPE_ERROR_TAG.error,
      error_code: [updateTrackPackageApiError.value.errorCode || PPE_ERROR_TAG.defaultErrorCode],
      error_message: [updateTrackPackageApiError.value.message],
    });
  }
};

const scheduleDelivery = async () => {
  window.open(PPE_SCHEDULE_DELIVERY_URL, '_self');
  fireTag('link', {
    site_action: TRACK_PACKAGE.scheduleDeliveryTag.site_action,
    action_location: TRACK_PACKAGE.scheduleDeliveryTag.action_location,
  });
};

const getCurbsidePickup = () => {
  fireTag('link', {
    ...TRACK_PACKAGE.curbsideTag,
  });
};

const openConfirmCancelItemModal = async () => {
  fireTag('link', {
    site_action: TRACK_PACKAGE.confirmCancelTag.site_action,
    action_location: TRACK_PACKAGE.confirmCancelTag.action_location,
  });
  registerModal({
    id: 'backOrderCancelItemOverlayTrackPackage',
    title: 'Cancel Item?',
    width: '410px',
    component: markRaw(defineAsyncComponent(() => import('@/components/ppe/BackOrderCancelItemOverlayTrackPackage'))),
  });
  const itemDetails = {
    itemName: killSwitch.value?.ppeTrackPackageResponseOrderBasedEnabled
      ? trackPackage.value?.orders?.[0]?.shipments?.[0]?.items?.[0]?.productName
      : trackPackage.value?.items?.[0]?.productName,
    reservationNumber: trackPackage.value?.reservationNumber?.[0],
    sortIdentifier: trackPackage.value?.sortIdentifier,
    orderToken: route.query.o,
    lineNumber: trackPackage.value?.lineNumber,
    orderId: trackPackage.value?.orders?.[0]?.shipments?.[0]?.items?.[0]?.orderId ?? trackPackage.value?.orders?.[0]?.orderId,
  };
  orderDetailsStore.SET_ITEM_DETAILS_OVERLAY(itemDetails);
  toggleModal('backOrderCancelItemOverlayTrackPackage');
};

const waitForItem = async () => {
  fireTag('link', {
    site_action: TRACK_PACKAGE.waitForItemTag.site_action,
    action_location: TRACK_PACKAGE.waitForItemTag.action_location,
    custom_link_name: TRACK_PACKAGE.waitForItemTag.custom_link_name,
  });
  const apiBody = {
    willingToWaitInd: trackPackage.value?.sortIdentifier === 'backordered_2a' ? 'F' : 'I',
    lineNumber: trackPackage.value?.lineNumber,
  };

  if (trackPackage.value?.contact?.encryptedEmail) {
    apiBody.encryptedEmail = trackPackage.value.contact.encryptedEmail;
  } else {
    apiBody.email = trackPackage.value?.contact?.emailAddress;
  }

  if (trackPackage.value?.contact?.encryptedHomePhone) {
    apiBody.encryptedHomePhone = trackPackage.value.contact.encryptedHomePhone;
  } else {
    apiBody.phoneNumber = trackPackage.value?.contact?.homePhone;
  }

  if (killSwitch.value?.fedfilToUopMigrationWaitForItemEnabled) {
    apiBody.uopOrderId = trackPackage.value?.orders?.[0]?.shipments?.[0]?.items?.[0]?.orderId ?? trackPackage.value?.orders?.[0]?.orderId;
  }

  const payload = {
    reservationNumber: trackPackage.value?.reservationNumber?.[0],
    orderToken: route.query.o,
    orderNumber: trackPackage.value?.orderNumber?.[0],
    isSignedIn,
    userId: Cookie.get(onlineUid),
    isTrackPackageCall: true,
    shipmentToken: route.query.s,
    trackingNos: route.query.trackingNos,
    fedfilToUopMigrationWaitForItemEnabled: killSwitch.value?.fedfilToUopMigrationWaitForItemEnabled,
  };

  await backOrderConfirmationStore.GET_BACKORDER_CONFIRMATION({
    payload,
    reqBody: apiBody,
  });
};

onMounted(async () => {
  useTitle(`${TRACK_PACKAGE.titleText} - ${brandName}`);
  await getTrackPackage();
  if (trackPackage.value?.shipmentStatusCode === 111) {
    fireTag('view', {
      ...TRACK_PACKAGE.backorderTag,
      customer_type: isSignedIn.value ? TRACK_PACKAGE.signedInText : TRACK_PACKAGE.guestText,
    });
  } else if (trackPackage.value?.shipmentStatusCode >= 201 && trackPackage.value?.shipmentStatusCode <= 209) {
    fireTag('view', {
      ...TRACK_PACKAGE.bigticketTag,
      customer_type: isSignedIn.value ? TRACK_PACKAGE.signedInText : TRACK_PACKAGE.guestText,
    });
  } else if (pageName.value[shipmentType.value]) {
    fireTag('view', {
      ...TRACK_PACKAGE.guestTag,
      page_name: pageName.value[shipmentType.value],
      customer_type: isSignedIn.value ? TRACK_PACKAGE.signedInText : TRACK_PACKAGE.guestText,
    });
  }
});

onUnmounted(() => {
  trackPackageStore.CLEAR_TRACK_PACKAGE();
  closeSlideout('tracking-details-slideout');
});
</script>
